import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Search, ChevronRight, ChevronLeft, User, PawPrint, Plus, Minus, Syringe, Stethoscope, Scissors, Package, ShoppingCart, Banknote, CreditCard, ToggleLeft, ToggleRight, Check, ClipboardList, FileText, Send, Weight } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { getCustomers, getClinicOperations, getProducts, createOperationsBatch, getParasutSettings, createBatchInvoice, addPetNote, addPetWeightRecord } from '../services/api';
import { toast } from 'sonner';
import PetAvatar from './PetAvatar';
import PetIcon from './PetIcon';

const INTERVAL_OPTIONS = [
  { label: '1 Hf', days: 7 },
  { label: '2 Hf', days: 14 },
  { label: '1 Ay', days: 30 },
  { label: '3 Ay', days: 90 },
  { label: '6 Ay', days: 180 },
  { label: '1 Yıl', days: 365 },
];

const TYPE_CFG = {
  vaccine: { icon: Syringe, color: 'bg-[#6b6de3]', label: 'Aşı' },
  treatment: { icon: Stethoscope, color: 'bg-[#FF9500]', label: 'Tedavi' },
  service: { icon: Scissors, color: 'bg-[#AF52DE]', label: 'Hizmet' },
};

const QuickOperationModal = ({ open, onClose, initialCustomer = null, initialPet = null }) => {
  const [step, setStep] = useState(1);

  // Step 1: Customer
  const [custQuery, setCustQuery] = useState('');
  const [custResults, setCustResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Step 2: Pet
  const [selectedPet, setSelectedPet] = useState(null);
  const [quickPetName, setQuickPetName] = useState('');
  const [quickPetSpecies, setQuickPetSpecies] = useState('dog');
  const [showQuickPet, setShowQuickPet] = useState(false);

  // Step 3: Operations + Products
  const [clinicOps, setClinicOps] = useState([]);
  const [products, setProducts] = useState([]);
  const [opSearch, setOpSearch] = useState('');
  const [activeTab, setActiveTab] = useState('vaccine');
  const [cart, setCart] = useState([]);
  const opSearchRef = useRef(null);
  const [pendingOp, setPendingOp] = useState(null);
  const [expandedCycleId, setExpandedCycleId] = useState(null); // for linked product selection

  // Step 4: Payment
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashAmount, setCashAmount] = useState('');
  const [cardAmount, setCardAmount] = useState('');
  const [isCredit, setIsCredit] = useState(false);
  const [creditCollected, setCreditCollected] = useState('');
  const [creditMethod, setCreditMethod] = useState('cash');
  const [saving, setSaving] = useState(false);

  // Invoice
  const [createInvoice, setCreateInvoice] = useState(false);
  const [tcKimlikNo, setTcKimlikNo] = useState('');
  const [parasutActive, setParasutActive] = useState(false);

  // Pet Note with operation
  const [addNote, setAddNote] = useState(false);
  const [noteText, setNoteText] = useState('');

  // Weight update with operation
  const [updateWeight, setUpdateWeight] = useState(false);
  const [weightKg, setWeightKg] = useState('');

  useEffect(() => {
    if (open) {
      const startStep = (initialCustomer && initialPet) ? 3 : (initialCustomer ? 2 : 1);
      setStep(startStep);
      setCustQuery(''); setCustResults([]);
      setSelectedCustomer(initialCustomer);
      setSelectedPet(initialPet);
      setCart([]); setOpSearch(''); setActiveTab('vaccine');
      setPaymentMethod('cash'); setCashAmount(''); setCardAmount('');
      setIsCredit(false); setCreditCollected(''); setCreditMethod('cash');
      setShowQuickPet(false); setQuickPetName(''); setQuickPetSpecies('dog');
      setPendingOp(null);
      setCreateInvoice(false); setTcKimlikNo('');
      setAddNote(false); setNoteText('');
      setUpdateWeight(false); setWeightKg('');
      getClinicOperations().then(r => setClinicOps(r.data)).catch(() => {});
      getProducts().then(r => setProducts(r.data)).catch(() => {});
      getParasutSettings().then(r => setParasutActive(r.data?.is_active || false)).catch(() => {});
    }
  }, [open, initialCustomer, initialPet]);

  // Customer search
  useEffect(() => {
    if (custQuery.length < 2) { setCustResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await getCustomers(custQuery); setCustResults(r.data); } catch (e) {}
    }, 300);
    return () => clearTimeout(t);
  }, [custQuery]);

  const total = cart.reduce((s, i) => s + (parseFloat(i.price) || 0) * i.quantity, 0);

  // Add to cart (with linked products support)
  const addToCart = useCallback((item, type) => {
    const id = `${type}-${item.id}`;
    // If it's an operation, check linked products
    if (type !== 'product') {
      const linked = item.linked_products || [];
      if (linked.length > 1) {
        // Multiple linked products - show selection
        setPendingOp(item);
        return;
      }
      // Auto-select single linked product or none
      let deductId = null;
      let deductName = null;
      let price = item.price || item.default_price || '';
      if (linked.length === 1) {
        deductId = linked[0].product_id;
        deductName = linked[0].product_name;
        const linkedProd = products.find(p => p.id === deductId);
        if (linkedProd?.default_price) price = linkedProd.default_price;
      }
      setCart(prev => {
        const existing = prev.find(c => c.id === id);
        if (existing) return prev.map(c => c.id === id ? { ...c, quantity: c.quantity + 1 } : c);
        let nextDue = null;
        const intervalDays = item.default_interval_days;
        if (type === 'vaccine' && intervalDays) {
          const d = new Date(); d.setDate(d.getDate() + intervalDays);
          nextDue = d.toISOString().split('T')[0];
        }
        return [...prev, { id, name: item.name, type, price: String(price), quantity: 1, deduct_product_id: deductId, deduct_product_name: deductName, next_due_date: nextDue, intervalDays, vat_rate: item.vat_rate }];
      });
    } else {
      // Direct product add
      setCart(prev => {
        const existing = prev.find(c => c.id === id);
        if (existing) return prev.map(c => c.id === id ? { ...c, quantity: c.quantity + 1 } : c);
        return [...prev, { id, name: item.name, type, price: String(item.price || item.default_price || ''), quantity: 1, deduct_product_id: null, deduct_product_name: null, next_due_date: null, intervalDays: null, vat_rate: item.vat_rate }];
      });
    }
    setOpSearch('');
  }, [products]);

  const handleLinkedProductSelect = (productId) => {
    if (!pendingOp) return;
    const linked = pendingOp.linked_products || [];
    const lp = linked.find(l => l.product_id === productId);
    const linkedProd = products.find(p => p.id === productId);
    const price = linkedProd?.default_price || pendingOp.default_price || '';
    const id = `${pendingOp.type}-${pendingOp.id}-${productId}`;
    setCart(prev => {
      const existing = prev.find(c => c.id === id);
      if (existing) return prev.map(c => c.id === id ? { ...c, quantity: c.quantity + 1 } : c);
      let nextDue = null;
      const intervalDays = pendingOp.default_interval_days;
      if (pendingOp.type === 'vaccine' && intervalDays) {
        const d = new Date(); d.setDate(d.getDate() + intervalDays);
        nextDue = d.toISOString().split('T')[0];
      }
      return [...prev, { id, name: pendingOp.name, type: pendingOp.type, price: String(price), quantity: 1, deduct_product_id: productId, deduct_product_name: lp?.product_name || linkedProd?.name || '', next_due_date: nextDue, intervalDays, vat_rate: pendingOp.vat_rate }];
    });
    setPendingOp(null);
    setOpSearch('');
    toast.success(`${pendingOp.name} eklendi`);
  };

  const updateCart = (id, field, val) => setCart(prev => prev.map(i => i.id === id ? { ...i, [field]: val } : i));
  const removeCart = (id) => setCart(prev => prev.filter(i => i.id !== id));

  // Quick pet
  const handleQuickPet = async () => {
    if (!quickPetName.trim()) { toast.error('Hasta adı gerekli'); return; }
    try {
      const { createPet } = await import('../services/api');
      const res = await createPet({ customer_id: selectedCustomer.id, name: quickPetName.trim(), species: quickPetSpecies });
      const newPet = { id: res.data.id, name: quickPetName.trim(), species: quickPetSpecies };
      setSelectedCustomer(prev => ({ ...prev, pets: [...(prev.pets || []), newPet] }));
      setSelectedPet(newPet);
      setShowQuickPet(false); setQuickPetName(''); setQuickPetSpecies('dog');
      setStep(3);
      toast.success(`${newPet.name} eklendi`);
    } catch (e) { toast.error('Hasta eklenemedi'); }
  };

  // Filtered ops/products
  const filteredOps = opSearch.length >= 1
    ? clinicOps.filter(o => o.name.toLowerCase().includes(opSearch.toLowerCase()))
    : clinicOps;
  const filteredProds = opSearch.length >= 1
    ? products.filter(p => p.name.toLowerCase().includes(opSearch.toLowerCase()) || (p.barcode && p.barcode.includes(opSearch)))
    : products;

  // Submit
  const handleSubmit = async () => {
    if (createInvoice && tcKimlikNo.length !== 11) {
      toast.error('Fatura kesmek için geçerli bir TC Kimlik No giriniz (11 hane)');
      return;
    }
    setSaving(true);
    try {
      const ops = cart.map(item => ({
        pet_id: selectedPet.id,
        type: item.type,
        name: item.name,
        price: (parseFloat(item.price) || 0) * item.quantity,
        quantity: item.quantity,
        unit_price: parseFloat(item.price) || 0,
        note: null,
        next_due_date: item.next_due_date || null,
        deduct_product_id: item.deduct_product_id,
        vat_rate: item.vat_rate,
      }));

      let finalCash = 0, finalCard = 0;
      if (paymentMethod === 'cash') { finalCash = total; }
      else if (paymentMethod === 'card') { finalCard = total; }
      else { finalCash = parseFloat(cashAmount) || 0; finalCard = parseFloat(cardAmount) || 0; }

      await createOperationsBatch({
        pet_id: selectedPet.id,
        operations: ops,
        payment_method: isCredit ? creditMethod : paymentMethod,
        cash_amount: finalCash,
        card_amount: finalCard,
        is_credit: isCredit,
        credit_collected: isCredit ? (parseFloat(creditCollected) || 0) : 0,
        credit_collected_method: isCredit ? creditMethod : 'cash',
      });

      if (createInvoice && parasutActive && selectedCustomer) {
        try {
          const invoiceItems = cart.map(c => ({
            name: c.name,
            price: parseFloat(c.price) || 0,
            quantity: c.quantity || 1,
            vat_rate: c.vat_rate != null ? parseFloat(c.vat_rate) : 20,
          }));
          await createBatchInvoice({
            customer_id: selectedCustomer.id,
            tc_kimlik_no: tcKimlikNo,
            items: invoiceItems,
          });
          toast.success('Fatura oluşturuldu');
        } catch (e) {
          toast.error('İşlemler kaydedildi ama fatura oluşturulamadı: ' + (e.response?.data?.detail || ''));
        }
      }

      toast.success('İşlem kaydedildi');

      // Save pet note if toggle is on
      if (addNote && noteText.trim() && selectedPet?.id) {
        try {
          const fd = new FormData();
          fd.append('text', noteText.trim());
          await addPetNote(selectedPet.id, fd);
        } catch (e) {
          toast.error('İşlem kaydedildi ama not eklenemedi');
        }
      }

      // Save weight if toggle is on
      if (updateWeight && weightKg && selectedPet?.id) {
        const kg = parseFloat(weightKg);
        if (kg > 0) {
          try {
            await addPetWeightRecord(selectedPet.id, { weight_grams: Math.round(kg * 1000) });
          } catch (e) {
            toast.error('İşlem kaydedildi ama kilo güncellenemedi');
          }
        }
      }

      onClose(true);
    } catch (e) {
      const errDetail = e.response?.data?.detail;
      const msg = typeof errDetail === 'string' ? errDetail : Array.isArray(errDetail) ? errDetail.map(d => d.msg).join(', ') : 'Kayıt başarısız';
      toast.error(msg);
    }
    setSaving(false);
  };

  if (!open) return null;

  const veresiyeRemaining = total - (parseFloat(creditCollected) || 0);

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3" data-testid="quick-operation-modal">
      <div className="absolute inset-0 bg-black/40" onClick={() => onClose(false)} />
      <div className="relative w-full sm:max-w-md lg:max-w-3xl bg-white sm:rounded-2xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] shadow-2xl">

        {/* Header */}
        <div className="px-4 lg:px-6 pt-4 pb-3 flex items-center justify-between border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            {step > 1 && (
              <button onClick={() => setStep(step - 1)} className="p-1 rounded-full hover:bg-gray-100">
                <ChevronLeft size={20} className="text-[#8E8E93]" />
              </button>
            )}
            <h2 className="text-base lg:text-lg font-bold text-gray-900">
              {step === 1 ? 'Müşteri Seç' : step === 2 ? 'Hasta Seç' : step === 3 ? 'İşlem Ekle' : 'Ödeme'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#8E8E93]">{step}/4</span>
            <button onClick={() => onClose(false)} className="p-1.5 rounded-full hover:bg-gray-100">
              <X size={22} className="text-[#8E8E93]" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">

          {/* Step 1: Customer */}
          {step === 1 && (
            <div className="p-4 lg:p-6 space-y-2 lg:space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={16} />
                <Input type="search" placeholder="Müşteri Ara (İsim, telefon veya TCKN)" value={custQuery}
                  onChange={e => setCustQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && custResults.length === 1) { setSelectedCustomer(custResults[0]); setStep(2); } }}
                  className="pl-9 h-11 text-sm bg-[#F2F2F7] border-0 rounded-xl" autoFocus data-testid="op-customer-search" />
              </div>
              {custResults.map(c => (
                <button key={c.id} onClick={() => { setSelectedCustomer(c); setStep(2); }}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-[#F2F2F7] rounded-xl hover:bg-gray-200 text-left"
                  data-testid={`op-customer-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{c.name}</p>
                    <p className="text-xs text-[#8E8E93]">{c.phone}</p>
                    {c.pets?.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        {c.pets.map(pet => (
                          <span key={pet.id} className="text-[11px] text-[#6b6de3] flex items-center gap-0.5">
                            <PetAvatar species={pet.species} gender={pet.gender} size={10} /> {pet.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ChevronRight size={18} className="text-[#C7C7CC] shrink-0" />
                </button>
              ))}
              {custQuery.length >= 2 && custResults.length === 0 && (
                <div className="text-center py-4" data-testid="customer-not-found">
                  <p className="text-sm text-[#8E8E93] mb-2">Müşteri bulunamadı</p>
                  <Button onClick={() => { onClose(false); window.dispatchEvent(new CustomEvent('openQuickCustomer')); }}
                    className="bg-[#6b6de3] hover:bg-[#5658c4] rounded-xl text-xs h-9 px-4"
                    data-testid="add-new-customer-btn">
                    <Plus size={14} className="mr-1" /> Yeni Müşteri Ekle
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Pet */}
          {step === 2 && (
            <div className="p-4 lg:p-6 space-y-2 lg:space-y-3">
              <div className="bg-blue-50 rounded-xl p-3 flex items-center gap-2 mb-1">
                <User size={16} className="text-[#6b6de3]" />
                <span className="text-sm font-medium text-gray-900">{selectedCustomer?.name}</span>
              </div>
              {selectedCustomer?.pets?.map(pet => (
                <button key={pet.id} onClick={() => { setSelectedPet(pet); setStep(3); }}
                  className="w-full p-3 bg-[#F2F2F7] rounded-xl flex items-center gap-3 hover:bg-gray-200 transition-colors"
                  data-testid={`op-pet-${pet.id}`}>
                  <div className="w-9 h-9 rounded-full flex items-center justify-center">
                    <PetAvatar species={pet.species} gender={pet.gender} size={18} />
                  </div>
                  <span className="font-medium text-gray-900 text-sm">{pet.name}</span>
                </button>
              ))}
              {!showQuickPet ? (
                <button onClick={() => setShowQuickPet(true)}
                  className="w-full p-3 border-2 border-dashed border-[#C7C7CC] rounded-xl flex items-center justify-center gap-2 text-[#6b6de3] font-medium text-sm"
                  data-testid="op-quick-add-pet">
                  <Plus size={16} /> Yeni Hasta Ekle
                </button>
              ) : (
                <div className="bg-blue-50 rounded-xl p-3 space-y-2">
                  <Input placeholder="Hasta adı" value={quickPetName} onChange={e => setQuickPetName(e.target.value)}
                    className="h-10 text-sm bg-white border-0 rounded-xl" autoFocus />
                  <div className="flex gap-2">
                    {[{ v: 'dog', l: 'Köpek' }, { v: 'cat', l: 'Kedi' }, { v: 'bird', l: 'Kuş' }, { v: 'other', l: 'Diğer' }].map(s => (
                      <button key={s.v} onClick={() => setQuickPetSpecies(s.v)}
                        className={`flex-1 py-2 rounded-xl text-xs font-medium transition-colors ${quickPetSpecies === s.v ? 'bg-[#6b6de3] text-white' : 'bg-white text-gray-700'}`}>{s.l}</button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" className="flex-1 h-9 text-sm rounded-xl" onClick={() => setShowQuickPet(false)}>Vazgeç</Button>
                    <Button className="flex-1 h-9 text-sm bg-[#6b6de3] rounded-xl" onClick={handleQuickPet}>Ekle</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Operations & Products */}
          {step === 3 && (
            <div className="p-4 lg:p-6 space-y-3">
              {/* Info bar */}
              <div className="flex items-center gap-2 text-xs text-[#8E8E93]">
                <User size={12} className="text-[#6b6de3]" />{selectedCustomer?.name}
                <span>·</span>
                <PetAvatar species={selectedPet?.species} gender={selectedPet?.gender} size={10} />{selectedPet?.name}
              </div>

              {/* 2x2 Category Grid */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'vaccine', label: 'Aşı', icon: Syringe, color: 'bg-[#6b6de3]' },
                  { key: 'treatment', label: 'Tedavi', icon: Stethoscope, color: 'bg-[#FF9500]' },
                  { key: 'service', label: 'Hizmet', icon: Scissors, color: 'bg-[#AF52DE]' },
                  { key: 'product', label: 'Ürün', icon: Package, color: 'bg-[#34C759]' },
                ].map(cat => {
                  const count = cat.key === 'product'
                    ? products.length
                    : clinicOps.filter(o => o.type === cat.key).length;
                  const Icon = cat.icon;
                  return (
                    <button key={cat.key} onClick={() => setActiveTab(cat.key)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-left transition-all ${
                        activeTab === cat.key
                          ? `${cat.color} text-white shadow-sm`
                          : 'bg-[#F2F2F7] text-gray-700'
                      }`} data-testid={`cat-${cat.key}`}>
                      <Icon size={16} />
                      <div>
                        <p className="text-xs font-semibold leading-tight">{cat.label}</p>
                        <p className={`text-[10px] ${activeTab === cat.key ? 'text-white/70' : 'text-[#8E8E93]'}`}>{count} kayıt</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={14} />
                <Input ref={opSearchRef} type="search"
                  placeholder={activeTab === 'product' ? 'Barkod veya ürün adı...' : `${activeTab === 'vaccine' ? 'Aşı' : activeTab === 'treatment' ? 'Tedavi' : 'Hizmet'} ara...`}
                  value={opSearch} onChange={e => setOpSearch(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      if (activeTab === 'product') {
                        const barcodeMatch = products.find(p => p.barcode && p.barcode === opSearch.trim());
                        if (barcodeMatch) { addToCart(barcodeMatch, 'product'); toast.success(`${barcodeMatch.name} sepete eklendi`); }
                        else if (filteredProds.length === 1) { addToCart(filteredProds[0], 'product'); toast.success(`${filteredProds[0].name} sepete eklendi`); }
                      } else {
                        const matches = filteredOps.filter(o => o.type === activeTab);
                        if (matches.length === 1) { addToCart(matches[0], matches[0].type); }
                      }
                    }
                  }}
                  className="pl-8 h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl" data-testid="op-search-input" />
              </div>

              {/* Linked Product Selection */}
              {pendingOp && (
                <div className="bg-blue-50 rounded-xl p-3 space-y-2" data-testid="linked-product-select">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-gray-700">
                      <Package size={12} className="inline mr-1 text-[#6b6de3]" />
                      {pendingOp.name} — Ürün Seçin
                    </p>
                    <button onClick={() => setPendingOp(null)} className="text-xs text-[#FF3B30] font-medium">Vazgeç</button>
                  </div>
                  {(pendingOp.linked_products || []).map(lp => {
                    const prod = products.find(p => p.id === lp.product_id);
                    return (
                      <button key={lp.product_id} onClick={() => handleLinkedProductSelect(lp.product_id)}
                        className="w-full flex items-center justify-between px-3 py-2 bg-white rounded-lg hover:bg-gray-50 text-left"
                        data-testid={`linked-product-${lp.product_id}`}>
                        <div>
                          <p className="text-xs font-medium text-gray-900">{lp.product_name}</p>
                          {prod && <p className="text-[10px] text-[#8E8E93]">Stok: {prod.current_stock} · {prod.default_price || 0} TL</p>}
                        </div>
                        <Plus size={14} className="text-[#6b6de3]" />
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Items list */}
              {!pendingOp && (
              <div className="space-y-1 max-h-36 lg:max-h-52 overflow-y-auto">
                {activeTab !== 'product' ? (
                  filteredOps.filter(o => o.type === activeTab).map(op => {
                    const cfg = TYPE_CFG[op.type] || TYPE_CFG.service;
                    const Icon = cfg.icon;
                    const hasLinked = (op.linked_products || []).length > 0;
                    return (
                      <button key={op.id} onClick={() => addToCart(op, op.type)}
                        className="w-full flex items-center gap-2 px-2.5 py-2 bg-[#F2F2F7] rounded-xl hover:bg-gray-200 text-left text-sm"
                        data-testid={`op-item-${op.id}`}>
                        <div className={`w-6 h-6 ${cfg.color} rounded-md flex items-center justify-center text-white shrink-0`}><Icon size={12} /></div>
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-medium text-gray-900 truncate block">{op.name}</span>
                          {hasLinked && <span className="text-[9px] text-[#6b6de3]">{op.linked_products.length} bağlı ürün</span>}
                          {op.default_interval_days && <span className="text-[9px] text-[#34C759]"> · {op.default_interval_days >= 365 ? `${Math.round(op.default_interval_days/365)} yıl` : op.default_interval_days >= 30 ? `${Math.round(op.default_interval_days/30)} ay` : `${op.default_interval_days} gün`}</span>}
                        </div>
                        <span className="text-xs text-[#8E8E93] shrink-0">{op.default_price ? `${op.default_price} TL` : ''}</span>
                        <Plus size={14} className="text-[#6b6de3] shrink-0" />
                      </button>
                    );
                  })
                ) : (
                  /* Product tab: only show search results, not full list */
                  opSearch.length >= 2 ? filteredProds.map(p => (
                    <button key={p.id} onClick={() => addToCart(p, 'product')}
                      className="w-full flex items-center gap-2 px-2.5 py-2 bg-[#F2F2F7] rounded-xl hover:bg-gray-200 text-left text-sm"
                      data-testid={`prod-item-${p.id}`}>
                      <Package size={14} className="text-[#34C759] shrink-0" />
                      <span className="flex-1 truncate text-xs font-medium text-gray-900">{p.name}</span>
                      <span className="text-xs text-[#8E8E93]">Stok:{p.current_stock} · {p.default_price || 0}₺</span>
                      <Plus size={14} className="text-[#6b6de3] shrink-0" />
                    </button>
                  )) : (
                    <p className="text-center text-xs text-[#8E8E93] py-4">Barkod veya ürün adı yazarak arayın.<br/>Barkod tam eşleşirse Enter ile sepete eklenir.</p>
                  )
                )}
                {activeTab !== 'product' && filteredOps.filter(o => o.type === activeTab).length === 0 && (
                  <p className="text-center text-xs text-[#8E8E93] py-3">Kayıt bulunamadı</p>
                )}
                {activeTab === 'product' && opSearch.length >= 2 && filteredProds.length === 0 && (
                  <p className="text-center text-xs text-[#8E8E93] py-3">Ürün bulunamadı</p>
                )}
              </div>
              )}

              {/* Cart */}
              {cart.length > 0 && (
                <div data-testid="op-cart">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-[#8E8E93] flex items-center gap-1"><ShoppingCart size={12} /> Sepet ({cart.length})</span>
                    <span className="text-sm font-bold text-gray-900">{total.toFixed(0)} TL</span>
                  </div>
                  <div className="space-y-1">
                    {cart.map(item => {
                      const cfg = TYPE_CFG[item.type] || { icon: Package, color: 'bg-[#FF9500]' };
                      const Icon = cfg.icon;
                      const cycleOpen = expandedCycleId === item.id;
                      return (
                        <div key={item.id} className="bg-[#F2F2F7] rounded-xl px-2.5 py-2">
                          <div className="flex items-center gap-1.5">
                            <div className={`w-5 h-5 ${cfg.color} rounded-md flex items-center justify-center text-white shrink-0`}><Icon size={10} /></div>
                            <div className="flex-1 min-w-0">
                              <span className="text-[11px] font-medium text-gray-900 truncate block">{item.name}</span>
                              {item.deduct_product_name && <span className="text-[9px] text-[#6b6de3] truncate block">{item.deduct_product_name}</span>}
                            </div>
                            <div className="flex items-center bg-white rounded-md shrink-0">
                              <button onClick={() => updateCart(item.id, 'quantity', Math.max(1, item.quantity - 1))} className="w-5 h-5 flex items-center justify-center text-[#8E8E93]"><Minus size={10} /></button>
                              <span className="w-4 text-center text-[10px] font-semibold">{item.quantity}</span>
                              <button onClick={() => updateCart(item.id, 'quantity', item.quantity + 1)} className="w-5 h-5 flex items-center justify-center text-[#6b6de3]"><Plus size={10} /></button>
                            </div>
                            <div className="relative w-[5rem] shrink-0">
                              <Input type="number" value={item.price} onChange={e => updateCart(item.id, 'price', e.target.value)}
                                className="h-6 text-[11px] bg-white border-0 rounded-md pr-4 text-right" />
                              <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[8px] text-[#8E8E93]">TL</span>
                            </div>
                            <button onClick={() => removeCart(item.id)} className="p-0.5 text-[#C7C7CC] hover:text-red-500 shrink-0"><X size={12} /></button>
                          </div>
                          {item.type === 'vaccine' && (
                            <div className="flex items-center gap-1.5 mt-1.5">
                              {item.next_due_date && (
                                <span className="text-[10px] font-medium text-[#34C759] bg-green-50 px-1.5 py-0.5 rounded">
                                  {new Date(item.next_due_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </span>
                              )}
                              <button onClick={() => setExpandedCycleId(cycleOpen ? null : item.id)}
                                className="text-[10px] font-medium text-[#6b6de3] hover:underline">
                                {cycleOpen ? 'Kapat' : 'Döngüyü Değiştir'}
                              </button>
                            </div>
                          )}
                          {item.type === 'vaccine' && cycleOpen && (
                            <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                              {INTERVAL_OPTIONS.map(opt => (
                                <button key={opt.days} onClick={() => {
                                  const d = new Date(); d.setDate(d.getDate() + opt.days);
                                  updateCart(item.id, 'next_due_date', d.toISOString().split('T')[0]);
                                  updateCart(item.id, 'intervalDays', opt.days);
                                }}
                                  className={`px-2 py-1 rounded-lg text-[10px] font-medium transition-colors ${
                                    item.intervalDays === opt.days ? 'bg-[#34C759] text-white' : 'bg-white text-gray-700'
                                  }`}>
                                  {opt.label}
                                </button>
                              ))}
                              <Input type="date" value={item.next_due_date || ''}
                                onChange={e => { updateCart(item.id, 'next_due_date', e.target.value); updateCart(item.id, 'intervalDays', null); }}
                                className="h-6 text-[10px] bg-white border-0 rounded-lg w-28 px-1.5" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Note Toggle */}
              {cart.length > 0 && (
                <div data-testid="op-note-section" className="space-y-2">
                  <div className="flex items-center justify-between bg-[#F2F2F7] rounded-xl p-3">
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-[#6b6de3]" />
                      <span className="text-xs font-semibold text-gray-700">Not Ekle</span>
                    </div>
                    <button onClick={() => setAddNote(!addNote)} data-testid="op-note-toggle">
                      {addNote
                        ? <ToggleRight size={32} className="text-[#6b6de3]" />
                        : <ToggleLeft size={32} className="text-[#C7C7CC]" />}
                    </button>
                  </div>
                  {addNote && (
                    <textarea
                      value={noteText}
                      onChange={e => setNoteText(e.target.value)}
                      placeholder="Hasta hakkında not yazın..."
                      className="w-full p-3 bg-[#F2F2F7] rounded-xl text-sm border-0 outline-none resize-none h-20 focus:ring-1 focus:ring-[#6b6de3]"
                      data-testid="op-note-input"
                    />
                  )}
                  <div className="flex items-center justify-between bg-[#F2F2F7] rounded-xl p-3">
                    <div className="flex items-center gap-2">
                      <Weight size={14} className="text-amber-500" />
                      <span className="text-xs font-semibold text-gray-700">Kilo Güncelle</span>
                    </div>
                    <button onClick={() => setUpdateWeight(!updateWeight)} data-testid="op-weight-toggle">
                      {updateWeight
                        ? <ToggleRight size={32} className="text-amber-500" />
                        : <ToggleLeft size={32} className="text-[#C7C7CC]" />}
                    </button>
                  </div>
                  {updateWeight && (
                    <div className="relative">
                      <input type="number" step="0.1" min="0" value={weightKg}
                        onChange={e => setWeightKg(e.target.value)}
                        placeholder="Ağırlık"
                        className="w-full p-3 pr-10 bg-[#F2F2F7] rounded-xl text-sm border-0 outline-none focus:ring-1 focus:ring-amber-500"
                        data-testid="op-weight-input" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8E8E93] font-medium">kg</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Payment */}
          {step === 4 && (
            <div className="p-4 lg:p-6 space-y-4">
              <div className="bg-[#F2F2F7] rounded-2xl p-4 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-[#8E8E93]">Toplam</span>
                  <span className="font-bold text-gray-900">{total.toFixed(0)} TL</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-[#8E8E93]">{cart.length} kalem</span>
                  <span className="text-[#8E8E93]">{selectedCustomer?.name} · {selectedPet?.name}</span>
                </div>
              </div>

              {/* Fatura Kes Toggle */}
              {parasutActive && (
                <div className="bg-[#F2F2F7] rounded-2xl p-4" data-testid="op-invoice-section">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Fatura Kes</p>
                      <p className="text-xs text-[#8E8E93]">Paraşüt ile e-Fatura / e-Arşiv</p>
                    </div>
                    <button onClick={() => setCreateInvoice(!createInvoice)} data-testid="op-invoice-toggle">
                      {createInvoice
                        ? <ToggleRight size={36} className="text-[#6366F1]" />
                        : <ToggleLeft size={36} className="text-[#C7C7CC]" />}
                    </button>
                  </div>
                  {createInvoice && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <label className="text-xs text-[#8E8E93] mb-1 block">TC Kimlik No <span className="text-red-500">*</span></label>
                      <Input type="text" inputMode="numeric" maxLength={11} value={tcKimlikNo}
                        onChange={e => setTcKimlikNo(e.target.value.replace(/\D/g, '').slice(0, 11))}
                        placeholder="11 haneli TC Kimlik No"
                        className="h-12 bg-white border-0 rounded-xl" data-testid="op-tc-kimlik-input" />
                      {tcKimlikNo && tcKimlikNo.length !== 11 && (
                        <p className="text-xs text-red-500 mt-1">TC Kimlik No 11 haneli olmalıdır</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Veresiye toggle */}
              <div className="flex items-center justify-between bg-[#F2F2F7] rounded-2xl p-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Veresiye</p>
                  <p className="text-[10px] text-[#8E8E93]">Borç olarak müşteriye yansır</p>
                </div>
                <button onClick={() => setIsCredit(!isCredit)} data-testid="op-veresiye-toggle">
                  {isCredit ? <ToggleRight size={36} className="text-[#FF9500]" /> : <ToggleLeft size={36} className="text-[#C7C7CC]" />}
                </button>
              </div>

              {isCredit ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-[#8E8E93] mb-1 block">Tahsil Edilen</label>
                    <div className="relative">
                      <Input type="number" placeholder="0" value={creditCollected} onChange={e => setCreditCollected(e.target.value)}
                        className="h-12 text-lg bg-[#F2F2F7] border-0 rounded-xl pr-8 text-right font-bold" data-testid="op-credit-input" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8E8E93]">TL</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {[{ v: 'cash', l: 'Nakit', i: Banknote }, { v: 'card', l: 'Kart', i: CreditCard }].map(m => (
                      <button key={m.v} onClick={() => setCreditMethod(m.v)}
                        className={`flex-1 h-11 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 ${creditMethod === m.v ? 'bg-[#6b6de3] text-white' : 'bg-[#F2F2F7] text-gray-700'}`}>
                        <m.i size={16} /> {m.l}
                      </button>
                    ))}
                  </div>
                  <div className="bg-orange-50 rounded-xl p-3 flex justify-between items-center">
                    <span className="text-xs text-[#FF9500]">Kalan Borç</span>
                    <span className="text-base font-bold text-[#FF3B30]">{veresiyeRemaining > 0 ? `${veresiyeRemaining.toFixed(0)} TL` : '0 TL'}</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    {[{ v: 'cash', l: 'Nakit', i: Banknote }, { v: 'card', l: 'Kart', i: CreditCard }, { v: 'split', l: 'Parçalı', i: null }].map(m => (
                      <button key={m.v} onClick={() => setPaymentMethod(m.v)}
                        className={`flex-1 h-12 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 ${paymentMethod === m.v ? 'bg-[#6b6de3] text-white' : 'bg-[#F2F2F7] text-gray-700'}`}>
                        {m.i && <m.i size={16} />} {m.l}
                      </button>
                    ))}
                  </div>
                  {paymentMethod === 'split' && (
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <label className="text-[10px] text-[#8E8E93] mb-0.5 block">Nakit</label>
                        <Input type="number" placeholder="0" value={cashAmount}
                          onChange={e => { setCashAmount(e.target.value); setCardAmount(String(total - (parseFloat(e.target.value) || 0))); }}
                          className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl pr-6 text-right" />
                        <span className="absolute right-2 bottom-2.5 text-[10px] text-[#8E8E93]">TL</span>
                      </div>
                      <div className="flex-1 relative">
                        <label className="text-[10px] text-[#8E8E93] mb-0.5 block">Kart</label>
                        <Input type="number" placeholder="0" value={cardAmount}
                          onChange={e => { setCardAmount(e.target.value); setCashAmount(String(total - (parseFloat(e.target.value) || 0))); }}
                          className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl pr-6 text-right" />
                        <span className="absolute right-2 bottom-2.5 text-[10px] text-[#8E8E93]">TL</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 lg:p-6 border-t border-gray-100 shrink-0">
          {step === 3 && (
            <Button onClick={() => setStep(4)} disabled={cart.length === 0}
              className="w-full h-11 bg-[#6b6de3] hover:bg-[#5658c4] rounded-xl text-sm font-semibold disabled:opacity-40">
              Devam ({total.toFixed(0)} TL) <ChevronRight size={16} className="ml-1" />
            </Button>
          )}
          {step === 4 && (
            <Button onClick={handleSubmit} disabled={saving}
              className="w-full h-11 bg-[#34C759] hover:bg-[#2DB44E] rounded-xl text-sm font-semibold disabled:opacity-40"
              data-testid="op-complete-btn">
              {saving ? 'Kaydediliyor...' : <><Check size={16} className="mr-1" /> Kaydet ({total.toFixed(0)} TL)</>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickOperationModal;
